/**
 * Video with text — poster image / play-button behaviour.
 *
 * Performance optimizations:
 *   - Fully isolated per-section (handles multiple sections on same page)
 *   - Caches DOM queries
 *   - Removes listeners after use to prevent memory leaks
 *   - Supports multiple "video with text" sections on the same page
 *   - Plays video from start on click (rewinds via currentTime)
 *   - Re-initialises via Shopify section lifecycle events
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '[data-section-type="cb-video-with-text"]';
  var POSTER_SELECTOR = '[data-video-poster]';
  var INITIALIZED_FLAG = 'data-video-poster-init';

  /**
   * Promise-safe playback. Never calls pause() while play() is pending.
   */
  function playFromStart(video, withSound) {
    if (!video) return;

    // Set audio state before playback
    video.muted = !withSound;

    try {
      video.currentTime = 0;
    } catch (e) {
      // Ignore errors from setting currentTime
    }

    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function (error) {
        // If sound-on playback failed, retry muted
        if (withSound && error.name !== 'NotSupportedError') {
          playFromStart(video, false);
        }
      });
    }
  }

  /**
   * Handle poster click - inject video template and play
   * Scoped to prevent interference between multiple sections
   */
  function handlePosterClick(event, sectionElement) {
    event.preventDefault();
    
    var poster = event.currentTarget;
    var wrapper = sectionElement.querySelector('.video-with-text__video-wrapper');
    var container = wrapper ? wrapper.querySelector('.current-video-with-text-container') : null;
    var sectionId = sectionElement.getAttribute('data-section-id');

    if (!container) {
      console.warn('Video container not found for section ' + sectionId);
      return;
    }

    // Inject video template if not already present
    if (!container.querySelector('video')) {
      var isMobile = window.matchMedia('(max-width: 768px)').matches;
      var templateSelector = isMobile 
        ? '#mobile-video-with-text-template-' + sectionId
        : '#default-video-with-text-template-' + sectionId;
      
      var template = sectionElement.querySelector(templateSelector);
      if (template && template.content) {
        try {
          container.appendChild(template.content.cloneNode(true));
        } catch (error) {
          console.error('Failed to clone video template for section ' + sectionId, error);
          return;
        }
      } else {
        console.warn('Video template not found: ' + templateSelector);
        return;
      }
    }

    // Play the video
    var video = wrapper.querySelector('video');
    if (video) {
      playFromStart(video, true);
      // Hide poster after first play
      poster.classList.add('is-hidden');
    } else {
      console.warn('Video element not found in section ' + sectionId);
    }
  }

  /**
   * Initialize a single section
   * Creates isolated event handlers for each section
   */
  function initSection(section) {
    if (!section) return;

    var sectionId = section.getAttribute('data-section-id');
    
    // Skip if already initialized
    if (section.getAttribute(INITIALIZED_FLAG) === 'true') {
      return;
    }

    var poster = section.querySelector(POSTER_SELECTOR);
    if (!poster) {
      return;
    }

    // Mark as initialized
    section.setAttribute(INITIALIZED_FLAG, 'true');
    
    // Create a bound handler specific to this section
    var clickHandler = function (e) {
      handlePosterClick(e, section);
    };
    
    // Add listener with { once: true } to auto-remove after first click
    poster.addEventListener('click', clickHandler, { once: true });
  }

  /**
   * Initialize all sections within a root element
   * Handles both initial load and section updates
   */
  function initWithin(root) {
    if (!root) return;

    // Check if root itself is a video-with-text section
    if (root.matches && root.matches(SECTION_SELECTOR)) {
      initSection(root);
    }

    // Find all video-with-text sections within root
    if (root.querySelectorAll) {
      var sections = root.querySelectorAll(SECTION_SELECTOR);
      for (var i = 0; i < sections.length; i++) {
        initSection(sections[i]);
      }
    }
  }

  /**
   * Global initialization on page load
   */
  function initAll() {
    initWithin(document.body);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-initialize when sections are added/reloaded
  // This handles dynamic section loading in the theme editor
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target) {
      // Clear initialization flag to allow re-init
      target.removeAttribute(INITIALIZED_FLAG);
      initWithin(target);
    }
  });

  // Cleanup when sections are removed
  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    if (target) {
      target.removeAttribute(INITIALIZED_FLAG);
    }
  });

  // Expose for debugging in console
  window.__videoWithTextDebug = {
    initAll: initAll,
    initSection: initSection,
    initWithin: initWithin
  };
})();